import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  EventBusy as EventBusyIcon,
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon,
  FilterList as FilterListIcon,
  PieChart as PieChartIcon
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp
} from 'firebase/firestore';

const AbsenceCalendar = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [absences, setAbsences] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateAbsences, setSelectedDateAbsences] = useState([]);
  const [openDayDialog, setOpenDayDialog] = useState(false);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const daysOfWeek = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  // Fonction utilitaire pour convertir les dates de manière sûre
  const toDate = (dateValue) => {
    if (!dateValue) return null;
    
    // Si c'est déjà un objet Date
    if (dateValue instanceof Date) {
      return dateValue;
    }
    
    // Si c'est un Timestamp Firestore avec la méthode toDate
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      return dateValue.toDate();
    }
    
    // Si c'est un objet avec seconds (format Timestamp)
    if (dateValue.seconds) {
      return new Date(dateValue.seconds * 1000);
    }
    
    // Sinon, essayer de créer une Date directement
    try {
      return new Date(dateValue);
    } catch (error) {
      console.error('Erreur lors de la conversion de date:', dateValue, error);
      return null;
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchAbsences();
    }
  }, [currentUser, currentDate]);

  const fetchAbsences = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'absences'),
        where('chefServiceId', '==', currentUser.uid)
      );

      const querySnapshot = await getDocs(q);
      let absencesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filtrer les absences du mois en cours côté client
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      startOfMonth.setHours(0, 0, 0, 0);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      endOfMonth.setHours(23, 59, 59, 999);
      
      absencesData = absencesData.filter(absence => {
        if (!absence.dateDebut || !absence.dateFin) return false;
        
        const startDate = toDate(absence.dateDebut);
        const endDate = toDate(absence.dateFin);
        
        if (!startDate || !endDate) return false;
        
        // Normaliser les dates
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        
        // Une absence est dans le mois si elle chevauche la période du mois
        return (startDate <= endOfMonth && endDate >= startOfMonth);
      });

      setAbsences(absencesData);
    } catch (error) {
      console.error('Erreur lors de la récupération des absences:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay() === 0 ? 7 : firstDay.getDay(); // Lundi = 1

    const days = [];
    
    // Ajouter les jours vides du début
    for (let i = 1; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Ajouter tous les jours du mois
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getAbsencesForDate = (date) => {
    if (!date) return [];
    
    // Normaliser la date du jour (mettre à minuit pour comparaison)
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    
    return absences.filter(absence => {
      if (!absence.dateDebut || !absence.dateFin) return false;
      
      const startDate = toDate(absence.dateDebut);
      const endDate = toDate(absence.dateFin);
      
      if (!startDate || !endDate) return false;
      
      // Normaliser les dates de début et fin (mettre à minuit)
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      
      // Vérifier si la date est dans la période d'absence
      return normalizedDate >= startDate && normalizedDate <= endDate;
    }).filter(absence => {
      if (filter === 'all') return true;
      return absence.statut === filter;
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'en attente': return '#ff9800';
      case 'approuvée': return '#4caf50';
      case 'refusée': return '#f44336';
      case 'signalée': return '#2196f3';
      default: return '#757575';
    }
  };

  const handleDateClick = (date) => {
    if (!date) return;
    
    const dayAbsences = getAbsencesForDate(date);
    setSelectedDate(date);
    setSelectedDateAbsences(dayAbsences);
    setOpenDayDialog(true);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Calculer les statistiques pour le graphique
  const chartData = useMemo(() => {
    const stats = {
      'En attente': { count: 0, color: '#ff9800' },
      'Approuvée': { count: 0, color: '#4caf50' },
      'Refusée': { count: 0, color: '#f44336' },
      'Signalée': { count: 0, color: '#2196f3' }
    };

    absences.forEach(absence => {
      const statut = absence.statut;
      if (statut === 'en attente') stats['En attente'].count++;
      else if (statut === 'approuvée') stats['Approuvée'].count++;
      else if (statut === 'refusée') stats['Refusée'].count++;
      else if (statut === 'signalée') stats['Signalée'].count++;
    });

    return Object.entries(stats)
      .filter(([_, data]) => data.count > 0)
      .map(([name, data]) => ({
        name,
        value: data.count,
        color: data.color
      }));
  }, [absences]);

  const totalAbsences = useMemo(() => {
    return absences.length;
  }, [absences]);

  const days = getDaysInMonth(currentDate);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* En-tête */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/chef-service/absences')}
        >
          Retour aux absences
        </Button>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarTodayIcon color="primary" />
          Calendrier des Absences
        </Typography>
      </Box>

      {/* Statistiques graphiques */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton onClick={() => navigateMonth(-1)}>
                  <ChevronLeftIcon />
                </IconButton>
                <Typography variant="h5" sx={{ minWidth: 200, textAlign: 'center' }}>
                  {months[currentDate.getMonth()]} {currentDate.getFullYear()}
                </Typography>
                <IconButton onClick={() => navigateMonth(1)}>
                  <ChevronRightIcon />
                </IconButton>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<TodayIcon />}
                  onClick={goToToday}
                >
                  Aujourd'hui
                </Button>
                
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Filtre</InputLabel>
                  <Select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    startAdornment={<FilterListIcon sx={{ mr: 1 }} />}
                  >
                    <MenuItem value="all">Toutes</MenuItem>
                    <MenuItem value="en attente">En attente</MenuItem>
                    <MenuItem value="approuvée">Approuvées</MenuItem>
                    <MenuItem value="refusée">Refusées</MenuItem>
                    <MenuItem value="signalée">Signalées</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Légende */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip 
                size="small" 
                label="En attente" 
                sx={{ bgcolor: '#ff9800', color: 'white' }}
              />
              <Chip 
                size="small" 
                label="Approuvée" 
                sx={{ bgcolor: '#4caf50', color: 'white' }}
              />
              <Chip 
                size="small" 
                label="Refusée" 
                sx={{ bgcolor: '#f44336', color: 'white' }}
              />
              <Chip 
                size="small" 
                label="Signalée" 
                sx={{ bgcolor: '#2196f3', color: 'white' }}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PieChartIcon color="primary" />
              Statistiques du mois
            </Typography>
            
            {totalAbsences === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Aucune absence ce mois
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ height: 200, mt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={70}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    <strong>Total: {totalAbsences}</strong> absence{totalAbsences > 1 ? 's' : ''}
                  </Typography>
                  {chartData.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: item.color, borderRadius: '50%' }} />
                        <Typography variant="body2">{item.name}</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="bold">{item.value}</Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Calendrier */}
      <Paper sx={{ p: 2 }}>
        {/* En-têtes des jours */}
        <Grid container spacing={1} sx={{ mb: 1 }}>
          {daysOfWeek.map((day) => (
            <Grid item xs key={day} sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                {day}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {/* Grille du calendrier */}
        <Grid container spacing={1}>
          {days.map((date, index) => {
            const dayAbsences = date ? getAbsencesForDate(date) : [];
            const isToday = date && date.toDateString() === new Date().toDateString();
            const hasAbsences = dayAbsences.length > 0;

            return (
              <Grid item xs key={index}>
                <Card
                  sx={{
                    minHeight: 100,
                    cursor: date ? 'pointer' : 'default',
                    bgcolor: date ? (isToday ? 'primary.light' : 'background.paper') : 'grey.100',
                    border: isToday ? 2 : 1,
                    borderColor: isToday ? 'primary.main' : 'divider',
                    '&:hover': date ? {
                      bgcolor: 'action.hover',
                      transform: 'scale(1.02)',
                      transition: 'all 0.2s'
                    } : {}
                  }}
                  onClick={() => handleDateClick(date)}
                >
                  <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    {date && (
                      <>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: isToday ? 'bold' : 'normal',
                            color: isToday ? 'primary.contrastText' : 'text.primary'
                          }}
                        >
                          {date.getDate()}
                        </Typography>
                        
                        {hasAbsences && (
                          <Box sx={{ mt: 0.5 }}>
                            <Badge 
                              badgeContent={dayAbsences.length} 
                              color="error"
                              sx={{ width: '100%' }}
                            >
                              <Box sx={{ width: '100%' }}>
                                {dayAbsences.slice(0, 2).map((absence, idx) => (
                                  <Box
                                    key={idx}
                                    sx={{
                                      height: 4,
                                      bgcolor: getStatusColor(absence.statut),
                                      borderRadius: 1,
                                      mb: 0.5,
                                      opacity: 0.8
                                    }}
                                  />
                                ))}
                                {dayAbsences.length > 2 && (
                                  <Typography variant="caption" color="text.secondary">
                                    +{dayAbsences.length - 2} autres
                                  </Typography>
                                )}
                              </Box>
                            </Badge>
                          </Box>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Dialog des détails du jour */}
      <Dialog 
        open={openDayDialog} 
        onClose={() => setOpenDayDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventBusyIcon />
          Absences du {selectedDate && formatDate(selectedDate)}
        </DialogTitle>
        <DialogContent>
          {selectedDateAbsences.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <EventBusyIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Aucune absence ce jour
              </Typography>
            </Box>
          ) : (
            <List>
              {selectedDateAbsences.map((absence) => (
                <ListItem key={absence.id} divider>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1">
                          {absence.nomPersonnel} {absence.prenomPersonnel}
                        </Typography>
                        <Chip 
                          label={absence.statut}
                          size="small"
                          sx={{ 
                            bgcolor: getStatusColor(absence.statut),
                            color: 'white'
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Type:</strong> {absence.type}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Période:</strong> {' '}
                          {toDate(absence.dateDebut)?.toLocaleDateString('fr-FR') || 'N/A'} - {' '}
                          {toDate(absence.dateFin)?.toLocaleDateString('fr-FR') || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Motif:</strong> {absence.motif}
                        </Typography>
                        {absence.validationComment && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Commentaire:</strong> {absence.validationComment}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDayDialog(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AbsenceCalendar;
